import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Input, Select, TimePicker, message, Row, Col, Space, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined, FieldTimeOutlined, TeamOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { scheduleApi } from '../api/schedule';
import { DoctorScheduleTemplate, ScheduleLocation } from '../api/model';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

// 星期几的映射
const weekdays = [
  { value: 1, label: '周一' },
  { value: 2, label: '周二' },
  { value: 3, label: '周三' },
  { value: 4, label: '周四' },
  { value: 5, label: '周五' },
  { value: 6, label: '周六' },
  { value: 0, label: '周日' },
];

const ScheduleTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<DoctorScheduleTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<DoctorScheduleTemplate | null>(null);
  const [selectedWeekday, setSelectedWeekday] = useState<number>(0); // 用于创建模板时记录当前星期几
  const [scheduleLocations, setScheduleLocations] = useState<ScheduleLocation[]>([]);
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();

  // 获取排班模板列表
  const fetchScheduleTemplates = async () => {
    setLoading(true);
    try {
      const response = await scheduleApi.listDoctorScheduleTemplates();
      if (response.success) {
        // 按weekday和start_time排序
        const sortedTemplates = response.data.sort((a, b) => {
          if (a.weekday !== b.weekday) {
            return a.weekday - b.weekday;
          }
          return a.start_time.localeCompare(b.start_time);
        });
        setTemplates(sortedTemplates);
      } else {
        message.error(response.message || '获取排班模板失败');
      }
    } catch (error) {
      console.error('获取排班模板失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取日程地点列表
  const fetchScheduleLocations = async () => {
    try {
      const response = await scheduleApi.listScheduleLocations();
      if (response.success) {
        setScheduleLocations(response.data);
      } else {
        message.error(response.message || '获取日程地点失败');
      }
    } catch (error) {
      console.error('获取日程地点失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 提交创建表单
  const handleCreateSubmit = async (values: any) => {
    try {
      // 格式化时间
      const startTime = values.start_time.format('HH:mm');
      const endTime = values.end_time.format('HH:mm');
      
      const requestData = {
        slot_name: values.slot_name,
        weekday: selectedWeekday, // 使用选中的星期几
        start_time: startTime,
        end_time: endTime,
        capacity: +values.capacity,
        schedule_location_id: values.schedule_location_id || null,
      };

      const response = await scheduleApi.createDoctorScheduleTemplate(requestData);
      if (response.success) {
        message.success('排班模板创建成功');
        setCreateModalVisible(false);
        form.resetFields();
        // 重新获取排班模板列表
        fetchScheduleTemplates();
      } else {
        message.error(response.message || '创建排班模板失败');
      }
    } catch (error) {
      console.error('创建排班模板失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 提交更新表单
  const handleUpdateSubmit = async (values: any) => {
    if (!currentTemplate) return;
    
    try {
      // 格式化时间
      const startTime = values.start_time.format('HH:mm');
      const endTime = values.end_time.format('HH:mm');
      
      // 注意：更新时weekday从currentTemplate获取，而不是表单值
      const requestData = {
        slot_name: values.slot_name,
        weekday: currentTemplate.weekday, // 固定为当前模板的星期几
        start_time: startTime,
        end_time: endTime,
        capacity: values.capacity,
        schedule_location_id: values.schedule_location_id || null,
      };

      const response = await scheduleApi.updateDoctorScheduleTemplate(currentTemplate.id, requestData);
      if (response.success) {
        message.success('排班模板更新成功');
        setUpdateModalVisible(false);
        updateForm.resetFields();
        setCurrentTemplate(null);
        // 重新获取排班模板列表
        fetchScheduleTemplates();
      } else {
        message.error(response.message || '更新排班模板失败');
      }
    } catch (error) {
      console.error('更新排班模板失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 删除排班模板
  const handleDelete = async (templateId: string) => {
    try {
      const response = await scheduleApi.deleteDoctorScheduleTemplate(templateId);
      if (response.success) {
        message.success('排班模板删除成功');
        // 重新获取排班模板列表
        fetchScheduleTemplates();
      } else {
        message.error(response.message || '删除排班模板失败');
      }
    } catch (error) {
      console.error('删除排班模板失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 初始化获取数据
  useEffect(() => {
    fetchScheduleTemplates();
    fetchScheduleLocations();
  }, []);

  // 打开更新模态框
  const openUpdateModal = (template: DoctorScheduleTemplate) => {
    setCurrentTemplate(template);
    setUpdateModalVisible(true);
    // 设置表单初始值
    updateForm.setFieldsValue({
      slot_name: template.slot_name,
      // weekday 不再需要设置，因为会显示为只读
      start_time: dayjs(template.start_time, 'HH:mm'),
      end_time: dayjs(template.end_time, 'HH:mm'),
      capacity: template.capacity,
      schedule_location_id: template.schedule_location_id || undefined,
    });
  };

  // 打开创建模态框
  const openCreateModal = (weekday: number) => {
    setSelectedWeekday(weekday);
    setCreateModalVisible(true);
    // 设置表单初始值
    form.setFieldsValue({
      weekday: weekday
    });
  };

  // 将模板按星期分组
  const groupTemplatesByWeekday = () => {
    const grouped: Record<number, DoctorScheduleTemplate[]> = {};
    
    // 初始化所有星期
    weekdays.forEach(day => {
      grouped[day.value] = [];
    });
    
    // 分组模板
    templates.forEach(template => {
      if (!grouped[template.weekday]) {
        grouped[template.weekday] = [];
      }
      grouped[template.weekday].push(template);
    });
    
    return grouped;
  };

  const groupedTemplates = groupTemplatesByWeekday();

  return (
    <div>
      <h2>排班模板管理</h2>
        <Row gutter={16}>
          {weekdays.map(day => (
            <Col flex="1 1 1px" key={day.value}>
              <Card 
                size="small" 
                title={day.label} 
                style={{ height: '100%' }}
                extra={
                  <Button 
                    type="link" 
                    icon={<PlusOutlined />} 
                    onClick={() => openCreateModal(day.value)}
                    size="small"
                  >
                    增加排班
                  </Button>
                }
              >
                {groupedTemplates[day.value].map(template => (
                  <Card 
                    key={template.id} 
                    size="small" 
                    style={{ marginBottom: 12 }}
                    title={template.slot_name}
                    extra={
                      <Space size="small">
                        <Button 
                          type="link" 
                          icon={<EditOutlined />} 
                          onClick={() => openUpdateModal(template)}
                          size="small"
                        />
                        <Button 
                          type="link" 
                          icon={<DeleteOutlined />} 
                          danger
                          onClick={() => handleDelete(template.id)}
                          size="small"
                        />
                      </Space>
                    }
                  >
                    <div>
                      <div><EnvironmentOutlined /> {template.schedule_location?.name || '未指定'}</div>
                      <div><ClockCircleOutlined /> {template.start_time} - {template.end_time}</div>
                      <div><TeamOutlined /> 可预约: <strong>{template.capacity}</strong></div>
                    </div>
                  </Card>
                ))}
              </Card>
            </Col>
          ))}
        </Row>

      {/* 创建排班模板模态框 */}
      <Modal
        title="新建排班模板"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="创建"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          <Form.Item name="weekday">
            {weekdays.find(w => w.value === selectedWeekday)?.label}
          </Form.Item>

          <Form.Item
            name="slot_name"
            label="模板名称"
            rules={[{ required: true, message: '请输入模板名称' }]}
          >
            <Input placeholder="请输入模板名称" />
          </Form.Item>

          <Form.Item
            name="start_time"
            label="开始时间"
            rules={[{ required: true, message: '请选择开始时间' }]}
          >
            <TimePicker format="HH:mm" placeholder="请选择开始时间" style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="end_time"
            label="结束时间"
            rules={[{ required: true, message: '请选择结束时间' }]}
          >
            <TimePicker format="HH:mm" placeholder="请选择结束时间" style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="capacity"
            label="可预约人数"
            rules={[{ required: true, message: '请输入可预约人数' }]}
          >
            <Input type="number" min="0" />
          </Form.Item>
          <Form.Item name="schedule_location_id" label="日程地点">
            <Select placeholder="请选择日程地点" allowClear>
              {scheduleLocations.map(location => (
                <Select.Option key={location.id} value={location.id}>
                  {location.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 更新排班模板模态框 */}
      <Modal
        title="编辑排班模板"
        open={updateModalVisible}
        onCancel={() => {
          setUpdateModalVisible(false);
          updateForm.resetFields();
          setCurrentTemplate(null);
        }}
        onOk={() => updateForm.submit()}
        okText="更新"
        cancelText="取消"
      >
        <Form form={updateForm} layout="vertical" onFinish={handleUpdateSubmit}>
          <Form.Item
            name="weekday"
          >
            {currentTemplate ? weekdays.find(w => w.value === currentTemplate.weekday)?.label : ''} 
          </Form.Item>

          <Form.Item
            name="slot_name"
            label="模板名称"
            rules={[{ required: true, message: '请输入模板名称' }]}
          >
            <Input placeholder="请输入模板名称" />
          </Form.Item>

          <Form.Item
            name="start_time"
            label="开始时间"
            rules={[{ required: true, message: '请选择开始时间' }]}
          >
            <TimePicker format="HH:mm" placeholder="请选择开始时间" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="end_time"
            label="结束时间"
            rules={[{ required: true, message: '请选择结束时间' }]}
          >
            <TimePicker format="HH:mm" placeholder="请选择结束时间" style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="capacity"
            label="可预约人数"
            rules={[{ required: true, message: '请输入可预约人数' }]}
          >
            <Input type="number" placeholder="请输入可预约人数" />
          </Form.Item>

          <Form.Item name="schedule_location_id" label="日程地点">
            <Select placeholder="请选择日程地点" allowClear>
              {scheduleLocations.map(location => (
                <Select.Option key={location.id} value={location.id}>
                  {location.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ScheduleTemplates;